import type { LLMMessage } from '@/IsekaiRPG/modules/llm/llmTypes';
import { klona } from 'klona';
import { MacroResolver } from './promptCompiler/MacroResolver';
import { STNativeMacroResolver } from './promptCompiler/STNativeMacroResolver';
import { PostProcessor } from './promptCompiler/PostProcessor';
import { PromptAssembler } from './promptCompiler/PromptAssembler';
import { STVariableMacroResolver } from './promptCompiler/STVariableMacroResolver';
import {
  type CompileOptions,
  type STPromptCompilerResult,
  type STPromptCompilerTrace,
  type STPromptCompilerTraceEntry,
} from './promptCompiler/types';
import { WorldBookMatcher } from './promptCompiler/WorldBookMatcher';

export type { CompileOptions, STPromptCompilerResult, STPromptCompilerTrace, STPromptCompilerTraceEntry };

export class STPromptCompiler {
  static compile(options: CompileOptions): LLMMessage[] {
    return this.compileWithTrace(options).messages;
  }

  static compileWithTrace(options: CompileOptions): STPromptCompilerResult {
    return this.compileInternal(options);
  }

  private static compileInternal(options: CompileOptions): STPromptCompilerResult {
    const macroContext = MacroResolver.normalizeMacroContext(options.macroContext);
    const rng = options.rng ?? Math.random;
    const runtimeMessages = MacroResolver.applyMacrosToMessages(klona(options.runtimeMessages), macroContext);
    const compileVariableState = STVariableMacroResolver.createState();
    const presetWithVariables = STVariableMacroResolver.applyToPreset(
      MacroResolver.applyMacrosToPreset(klona(options.preset), macroContext),
      compileVariableState,
    );
    const preset = STNativeMacroResolver.applyToPreset(presetWithVariables, rng);
    const worldBook = MacroResolver.applyMacrosToWorldBook(klona(options.worldBook), macroContext);
    const generationType = (options.generationType ?? 'normal').trim().toLowerCase();
    const characterContext = {
      names: (options.characterContext?.names ?? []).map(name => name.trim()).filter(Boolean),
      tags: (options.characterContext?.tags ?? []).map(tag => tag.trim()).filter(Boolean),
    };

    if (runtimeMessages.length === 0) {
      return {
        messages: runtimeMessages,
        trace: {
          generationType,
          worldBookEnabled: !!worldBook?.enabled,
          matchedCount: 0,
          totalEntries: worldBook?.entries?.length ?? 0,
          entries: [],
        },
      };
    }

    const { matchedEntries: matchedWorldBookEntries, traceEntries } = WorldBookMatcher.collectMatchedWorldBookEntries(
      runtimeMessages,
      worldBook,
      generationType,
      characterContext,
      options.rng,
    );
    const matchedWorldBookEntriesWithVariables = STVariableMacroResolver.applyToWorldBookEntries(
      matchedWorldBookEntries,
      compileVariableState,
    );
    const matchedWorldBookEntriesWithNativeMacros = STNativeMacroResolver.applyToWorldBookEntries(
      matchedWorldBookEntriesWithVariables,
      rng,
    );
    const splitWorldBook = WorldBookMatcher.splitWorldBookEntries(matchedWorldBookEntriesWithNativeMacros);
    const merged = PromptAssembler.assemble(runtimeMessages, preset, splitWorldBook, generationType, {
      markerPayloads: options.markerPayloads,
      mainPromptOverride: options.mainPromptOverride,
      regexRules: options.regexRules ?? [],
      macroContext,
    });
    const postType = options.customPromptPostProcessing ?? preset.custom_prompt_post_processing;
    const messages = PostProcessor.postProcessPrompt(merged, postType);

    return {
      messages,
      trace: {
        generationType,
        worldBookEnabled: !!worldBook?.enabled,
        matchedCount: matchedWorldBookEntries.length,
        totalEntries: worldBook?.entries?.length ?? 0,
        entries: traceEntries,
      },
    };
  }
}
